import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Emoji } from './implementations/@impl/Emoji.jsx';
import connectEmojiState from './connectEmojiState';
import { filter, map, share } from 'rxjs';
import { displayQueue } from 'store/slices/emoji/displayQueue';
import { actions as historyAction } from 'store/slices/emoji/historySlice';
import { createChunk } from 'store/slices/emoji/createChunk';
import { createFeed } from './lib/ET/et-feed';
import { createTarget } from '@megogo/et';
import scopeLogger from '../../lib/scope-logger';
import { isFeatureEnabled } from '#/modules/feature-flag/feature.jsx';

const console = scopeLogger('RMI:emoji');

const description = {
  id: 'emoji',
  version: 'legacy',
  Factory: function (description, dependencies) {
    const {
      store,
      // mns,
      API,
      // accessors,
    } = dependencies;
    const Implementation = connectEmojiState(Emoji);

    const emoji$ = API.mns.message$.pipe(
      filter(({ payload }) => payload.type === 'emoji' && payload.cmd === 'insert'),
      map(({ payload, ...rest }) => {
        return payload.data
      }),
      share(),
    );

    let historyOf;
    const history = new Map();
    const {
      options: {
        request_interval: chunkSize,
      }
    } = description;

    if (isFeatureEnabled('emoji-rating')) {
      emoji$.subscribe(({ positions }) => {
        positions.forEach(({
          start_time,
          emoji
        }) => {
          const chunkIndex = start_time - (start_time % chunkSize);
          if (!history.has(chunkIndex)) {
            history.set(chunkIndex, new Map());
          }
          const chunk = history.get(chunkIndex);
          if (!chunk.has(start_time)) {
            chunk.set(start_time, new Map());
          }

          const positionEntry = chunk.get(start_time);
          emoji.forEach(({
            name,
            count
          }) => {
            if (!positionEntry.has(name)) {
              positionEntry.set(name, 0);
            }
            positionEntry.set(name, positionEntry.get(name) + count);
          });
        });
      });
    }

    return {
      UI: ({ enabled, options, currentObject }) => {
        /* some kind special behaviour,
         this extension should keep internal state "mode" while on/off for the whole extension can change */
        const [silentMode, setSilentMode] = useState(true);
        /**
         *
         * @type {React.MutableRefObject<HTMLElement>}
         */
        const ref = useRef();

        const ET_feed = useMemo(() => {
          return createFeed(options.pack, options.icons);
        }, [options.icons, options.pack]);

        const ET_target = useMemo(() => {
          return createTarget(currentObject);
        }, [currentObject]);

        useEffect(() => {
          if (currentObject.object_id !== historyOf) {
            historyOf = currentObject.object_id;
            history.clear();
          }
        }, [currentObject]);

        useEffect(() => {
          console.log('silent mode changed', silentMode);
          if (silentMode) {
            return;
          }

          API.hostApp.trackEvent('impression', {
            target: ET_target,
            feed: ET_feed,
          });

          /* subscribe to MNS topic */
          return API.mns.subscribe(options.subscribe);
        }, [ET_feed, ET_target, silentMode, options.subscribe]);

        const sentCounter = useRef(new Map());

        const sendEmoji = useCallback(
          (name) => {
            if (!sentCounter.current.has(name)) {
              sentCounter.current.set(name, 0);
            }
            sentCounter.current.set(name, sentCounter.current.get(name) + 1);
            API.mns.publish(options.publish, { name });
            store.dispatch(
              displayQueue.actions.pushToQueue([
                {
                  name,
                  count: 1,
                  isOwn: true,
                },
              ]),
            );
          },
          [options.publish],
        );

        useEffect(() => {
          /* subscribe to MNS topic */
          const unsubscribe = API.mns.subscribe(options.subscribe);

          /* handle messages from topic */
          const subscription = emoji$
            .subscribe(({ positions }) => {
              positions.map(({
                start_time,
                emoji
              }) => {
                /* exclude from emojis events own sent, accordingly reduce own sent counter  */
                const excludeOwn = emoji.map((record) => {
                  const ownSend = sentCounter.current.get(record.name) || 0;
                  const recordCounter = record.count;
                  const count = Math.max(0, recordCounter - ownSend);
                  sentCounter.current.set(record.name, Math.max(0, ownSend - recordCounter));
                  return {
                    ...record,
                    count,
                  };
                });
                const startTime = new Date(start_time*1e3);
                return {
                  start_time,
                  __hr_start_time: `${startTime.toLocaleDateString()} ${startTime.toLocaleTimeString()}`,
                  emoji: excludeOwn.filter(({count}) => count > 0),
                };
              })
                .filter(({ emoji }) => emoji.length > 0)
                .forEach(({ start_time, emoji }) => {
                  const actualPosition = API.timeService.value?.position ?? Infinity;
                  if (actualPosition - start_time < 3) {
                    /* allow display income emoji not older than 3 seconds */
                    store.dispatch(displayQueue.actions.pushToQueue(emoji));
                  } else {
                    /* otherwise store in history */
                    store.dispatch(historyAction.appendRecords([{
                      emoji,
                      start_time,
                      chunk: createChunk(start_time, options.request_interval),
                    }]));
                  }
                });
            });

          return () => {
            unsubscribe();
            subscription.unsubscribe();
          };
        }, [options.subscribe]);

        if (!enabled) {
          return null;
        }

        return (
          <section
            data-role="interactive-extension"
            data-name="emoji"
            ref={ref}
          >
            <Implementation
              options={options}
              silentMode={silentMode}
              setSilentMode={setSilentMode}
              sendEmoji={sendEmoji}
              emoji$={emoji$}
              history={history}
            />
          </section>
        );
      },
    };
  },
};

export { connectEmojiState, description };
